<template>
  <div class="daily-missions" :style="{ '--mission-bar': theme.accentHex }">
    <header class="page-header">
      <div>
        <p class="kicker">{{ t('missions.kicker') }}</p>
        <h1>{{ t('missions.title') }}</h1>
        <p class="support">{{ lead }}</p>
      </div>
      <div class="header-actions">
        <button
          v-if="canCollectAll"
          type="button"
          class="claim"
          :disabled="missions.busy"
          @click="onCollectAll"
        >
          <CoinToken size="sm" shine bob />
          {{ t('missions.claimAll') }}
        </button>
        <router-link to="/" class="text-btn">
          {{ t('missions.back') }}
        </router-link>
      </div>
    </header>

    <div class="pane-tabs" role="tablist" :aria-label="t('missions.title')">
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'daily' }"
        :aria-selected="pane === 'daily'"
        @click="setPane('daily')"
      >
        {{ t('missions.tabs.daily') }}
        <span
          v-if="dailyPending > 0"
          class="tab-badge"
        >{{ dailyPending }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'runner' }"
        :aria-selected="pane === 'runner'"
        @click="setPane('runner')"
      >
        {{ t('missions.tabs.runner') }}
        <span
          v-if="missions.runnerPending > 0"
          class="tab-badge"
        >{{ missions.runnerPending }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'ranked' }"
        :aria-selected="pane === 'ranked'"
        @click="setPane('ranked')"
      >
        {{ t('missions.tabs.ranked') }}
        <span
          v-if="missions.rankedPending > 0"
          class="tab-badge"
        >{{ missions.rankedPending }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'collection' }"
        :aria-selected="pane === 'collection'"
        @click="setPane('collection')"
      >
        {{ t('missions.tabs.collection') }}
        <span
          v-if="missions.collectionPending > 0"
          class="tab-badge"
        >{{ missions.collectionPending }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'lastCall' }"
        :aria-selected="pane === 'lastCall'"
        @click="setPane('lastCall')"
      >
        {{ t('missions.tabs.lastCall') }}
      </button>
    </div>

    <MissionRewardRail v-show="pane === 'daily'" />

    <p v-if="pane !== 'lastCall'" class="reset">{{ resetLabel }}</p>
    <p v-else class="reset">{{ lastCallReset }}</p>

    <p v-if="(pane === 'daily' || pane === 'ranked' || pane === 'runner') && missions.loadError" class="empty">
      {{ t('missions.loadError') }}
      <button type="button" class="text-btn retry" @click="onRetry">
        {{ t('missions.retry') }}
      </button>
    </p>
    <p v-else-if="pane === 'daily' && missions.rows.length === 0" class="empty">
      {{ t('missions.empty') }}
    </p>

    <ul v-else-if="pane === 'daily'" class="list">
      <li
        v-for="row in missions.rows"
        :key="row.id"
        class="md-panel card"
        :class="{ done: row.done }"
        :data-mission-id="row.id"
      >
        <div class="card-top">
          <p class="cat">{{ t(`missions.category.${categoryOf(row.id)}`) }}</p>
          <span class="coin-prize">
            <CoinToken size="sm" />
            <span>×{{ coinReward }}</span>
          </span>
        </div>
        <h2>{{ t(`missions.ids.${row.id}`) }}</h2>
        <div class="bar" :aria-hidden="true">
          <span class="bar-fill" :style="{ width: `${pct(row)}%` }"></span>
        </div>
        <div class="card-foot">
          <p class="count">
            {{ t('missions.progress', { current: row.progress, target: row.target }) }}
          </p>
          <button
            v-if="isPending(row.id)"
            type="button"
            class="md-btn-tonal claim"
            :disabled="missions.busy"
            @click="onClaim(row.id, $event)"
          >
            <CoinToken size="sm" shine bob />
            {{ t('missions.claim') }}
          </button>
          <p v-else-if="row.done" class="claimed">
            {{ t('missions.claimed') }}
          </p>
        </div>
      </li>
    </ul>

    <ul v-else-if="pane === 'runner'" class="list">
      <li
        v-for="row in missions.runnerRows"
        :key="row.id"
        class="md-panel card"
        :class="{ done: row.done }"
      >
        <div class="card-top">
          <p class="cat">{{ t('missions.tabs.runner') }}</p>
          <span class="coin-prize">
            <CoinToken size="sm" />
            <span>×{{ coinReward }}</span>
          </span>
        </div>
        <h2>{{ t(`missions.runner.${row.id}`) }}</h2>
        <div class="bar" :aria-hidden="true">
          <span class="bar-fill" :style="{ width: `${pct(row)}%` }"></span>
        </div>
        <div class="card-foot">
          <p class="count">
            {{ t('missions.progress', { current: row.progress, target: row.target }) }}
          </p>
          <button
            v-if="row.done && !row.claimed"
            type="button"
            class="md-btn-tonal claim"
            :disabled="missions.busy"
            @click="onClaimRunner(row.id, $event)"
          >
            <CoinToken size="sm" shine bob />
            {{ t('missions.claim') }}
          </button>
          <p v-else-if="row.claimed" class="claimed">
            {{ t('missions.claimed') }}
          </p>
        </div>
      </li>
    </ul>

    <ul v-else-if="pane === 'ranked'" class="list">
      <li
        v-for="step in rankedSteps"
        :key="step"
        class="md-panel card"
        :class="{ done: rankedDone(step) }"
        :data-ranked-step="rankedPending(step) ? step : undefined"
      >
        <div class="card-top">
          <p class="cat">{{ t('missions.tabs.ranked') }}</p>
          <span class="prize">
            <span v-if="rankedCoins(step) > 0" class="coin-prize">
              <CoinToken size="sm" />
              <span>×{{ rankedCoins(step) }}</span>
            </span>
            <span v-if="rankedPack(step)" class="coin-prize">
              <span
                :ref="(el) => setPackNode(step, el)"
                class="pack-prize"
              >
                <img :src="packCover" alt="">
              </span>
              <span>×{{ rankedPackCount(step) }}</span>
            </span>
          </span>
        </div>
        <h2>{{ rankedTitle(step) }}</h2>
        <div class="bar" :aria-hidden="true">
          <span class="bar-fill" :style="{ width: `${rankedPct(step)}%` }"></span>
        </div>
        <div class="card-foot">
          <p class="count">
            {{ t('missions.progress', { current: rankedCurrent(step), target: step }) }}
          </p>
          <button
            v-if="rankedPending(step)"
            type="button"
            class="md-btn-tonal claim"
            :disabled="missions.busy"
            @click="onClaimRanked(step, $event)"
          >
            <CoinToken v-if="rankedCoins(step) > 0" size="sm" shine bob />
            {{ t('missions.claim') }}
          </button>
          <p v-else-if="rankedDone(step) && step <= missions.rankedCollected" class="claimed">
            {{ t('missions.claimed') }}
          </p>
        </div>
      </li>
    </ul>

    <div v-else-if="pane === 'collection'" class="collection-groups">
      <section
        v-for="family in collectionFamilies"
        :key="family"
      >
        <p class="family-title">{{ t(`missions.collection.family.${family}`) }}</p>
        <ul class="list">
          <li
            v-for="view in viewsOf(family)"
            :key="view.trackId"
            class="md-panel card"
            :class="{ done: view.finished, ready: view.pending > 0 }"
            :data-collection-pending="view.pending > 0 ? view.trackId : undefined"
          >
            <div class="card-top">
              <p class="cat">{{ t(`missions.collection.family.${view.family}`) }}</p>
              <span v-if="!view.finished" class="prize">
                <span v-if="view.focus.coins > 0" class="coin-prize">
                  <CoinToken size="sm" />
                  <span>×{{ view.focus.coins }}</span>
                </span>
                <span v-if="view.focus.packs > 0" class="coin-prize">
                  <span class="pack-prize">
                    <img :src="packCover" alt="">
                  </span>
                  <span>×{{ view.focus.packs }}</span>
                </span>
              </span>
            </div>
            <h2>{{ collectionTitle(view) }}</h2>
            <div class="rungs" :aria-hidden="true">
              <span
                v-for="step in view.rungs"
                :key="step.id"
                class="rung"
                :class="{
                  done: step.claimed,
                  ready: step.done && !step.claimed,
                }"
              ></span>
            </div>
            <div class="bar" :aria-hidden="true">
              <span class="bar-fill" :style="{ width: `${pct(view.focus)}%` }"></span>
            </div>
            <div class="card-foot">
              <p class="count">
                {{
                  t('missions.progress', {
                    current: Math.min(view.focus.progress, view.focus.target),
                    target: view.focus.target,
                  })
                }}
              </p>
              <button
                v-if="view.focus.done && !view.focus.claimed"
                type="button"
                class="md-btn-tonal claim"
                :disabled="missions.busy"
                @click="onClaimCollection(view, $event)"
              >
                <CoinToken v-if="view.focus.packs < 1" size="sm" shine bob />
                {{ t('missions.claim') }}
              </button>
              <p v-else-if="view.finished" class="claimed">
                {{ t('missions.claimed') }}
              </p>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <ul v-else-if="pane === 'lastCall'" class="list">
      <li
        v-for="step in lastCallSteps"
        :key="step.key"
        class="md-panel card"
        :class="{ done: lastCall.played >= step.at }"
      >
        <div class="card-top">
          <p class="cat">{{ t('missions.tabs.lastCall') }}</p>
        </div>
        <div class="reward-shot" :class="step.key">
          <AvatarView
            v-if="step.key === 'frame'"
            :config="frameLook"
            :size="96"
            :label="t('missions.lastCall.frame')"
          />
          <AvatarView
            v-else-if="step.key === 'portrait'"
            :config="portraitLook"
            :size="96"
            :label="t('missions.lastCall.portrait')"
          />
          <img
            v-else-if="step.key === 'sleeve'"
            src="/assets/sleeves/last-call.png"
            :alt="t('missions.lastCall.sleeve')"
          >
          <img
            v-else-if="step.key === 'mat'"
            class="mat"
            src="/assets/mats/last-call.png"
            :alt="t('missions.lastCall.mat')"
          >
          <img
            v-else
            class="pack"
            :src="promoCover"
            :alt="t('missions.lastCall.pack')"
          >
        </div>
        <h2>{{ t(`missions.lastCall.${step.key}`) }}</h2>
        <div class="bar" :aria-hidden="true">
          <span
            class="bar-fill"
            :style="{ width: `${lastCallPct(step.at)}%` }"
          ></span>
        </div>
        <div class="card-foot">
          <p class="count">
            {{
              t('missions.progress', {
                current: Math.min(lastCall.played, step.at),
                target: step.at,
              })
            }}
          </p>
          <p v-if="lastCall.played >= step.at" class="claimed">
            {{ t('missions.claimed') }}
          </p>
        </div>
      </li>
    </ul>

    <MissionPackWin
      v-if="showRankedWin"
      variant="ranked"
      :at="rankedWinStep"
      :pack-count="rankedWinPacks"
      :cover="packCover"
      :dx="rankedWinOrigin.dx"
      :dy="rankedWinOrigin.dy"
      @close="onRankedWinClose"
    />

    <MissionPackWin
      v-if="showCollectionWin"
      variant="collection"
      :pack-count="collectionWinPacks"
      :cover="packCover"
      :dx="collectionWinOrigin.dx"
      :dy="collectionWinOrigin.dy"
      @close="showCollectionWin = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  COLLECTION_FAMILIES,
  LAST_CALL_FRAME_AT,
  LAST_CALL_FRAME_ID,
  LAST_CALL_MAT_AT,
  LAST_CALL_PACK_AT,
  LAST_CALL_PORTRAIT_AT,
  LAST_CALL_PORTRAIT_ID,
  LAST_CALL_SLEEVE_AT,
  PROMO_PACK_ID,
  MISSION_COIN_REWARD,
  RANKED_DAILY_COUNT,
  RANKED_MID_PACK_STEP,
  rankedStepCoins,
  rankedStepGivesPack,
  rankedStepPacks,
  missionDef,
  msUntilUtcMidnight,
  type CollectionFamily,
  type CollectionTrackView,
  type MissionId,
} from '@shared/index'
import MissionRewardRail from '../components/MissionRewardRail.vue'
import MissionPackWin from '../components/MissionPackWin.vue'
import CoinToken from '../components/CoinToken.vue'
import AvatarView from '../components/AvatarView.vue'
import { flyCoinToWallet, missionCardEl } from '../components/coinFlight'
import { useMissionsStore } from '../stores/missions'
import { useThemeStore } from '../stores/theme'
import { useI18n } from '../i18n/t'
import { PACK_BY_ID, SHOP_PACK_ID } from '../game/packs'
import { useLastCallStore } from '../stores/lastCall'
import { usePlayerStore } from '../stores/player'

const RANKED_WIN_PREFIX = 'yugioh.ranked-pack-win:'
const rankedCount = RANKED_DAILY_COUNT
const coinReward = MISSION_COIN_REWARD
const packCover =
  PACK_BY_ID[SHOP_PACK_ID]?.cover ?? '/assets/packs/pack-master.webp'
const promoCover =
  PACK_BY_ID[PROMO_PACK_ID]?.cover ?? '/assets/packs/lob.jpg'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const missions = useMissionsStore()
const lastCall = useLastCallStore()
const player = usePlayerStore()
const frameLook = computed(() => ({
  ...player.avatar,
  frameId: LAST_CALL_FRAME_ID,
}))
const portraitLook = computed(() => ({
  cardId: LAST_CALL_PORTRAIT_ID,
  frameId: LAST_CALL_FRAME_ID,
}))
const theme = useThemeStore()
const remainingMs = ref(msUntilUtcMidnight())

type MissionPane = 'daily' | 'runner' | 'ranked' | 'collection' | 'lastCall'

function paneFromQuery(tab: unknown): MissionPane {
  if (tab === 'runner') return 'runner'
  if (tab === 'ranked') return 'ranked'
  if (tab === 'collection') return 'collection'
  if (tab === 'last-call') return 'lastCall'
  return 'daily'
}

const pane = ref<MissionPane>(paneFromQuery(route.query.tab))
const showRankedWin = ref(false)
const rankedWinStep = ref(RANKED_DAILY_COUNT)
const rankedWinPacks = ref(1)
const rankedWinOrigin = ref({ dx: 0, dy: 80 })
const showCollectionWin = ref(false)
const collectionWinPacks = ref(1)
const collectionWinOrigin = ref({ dx: 0, dy: 80 })
const rankedPackNodes = new Map<number, HTMLElement>()
let tick: ReturnType<typeof setInterval> | null = null

const canCollectAll = computed(() => {
  if (pane.value === 'collection') return missions.collectionPending >= 2
  if (pane.value !== 'daily') return false
  return missions.pendingCount + missions.railPending >= 2
})
const lastCallSteps = [
  { key: 'frame', at: LAST_CALL_FRAME_AT },
  { key: 'portrait', at: LAST_CALL_PORTRAIT_AT },
  { key: 'sleeve', at: LAST_CALL_SLEEVE_AT },
  { key: 'mat', at: LAST_CALL_MAT_AT },
  { key: 'pack', at: LAST_CALL_PACK_AT },
] as const
const lead = computed(() => {
  if (pane.value === 'runner') return t('missions.runner.lead')
  if (pane.value === 'ranked') return t('missions.rankedLead')
  if (pane.value === 'collection') return t('missions.collection.lead')
  if (pane.value === 'lastCall') {
    return lastCall.open ? t('missions.lastCallLead') : t('missions.lastCallClosed')
  }
  return t('missions.lead')
})

const collectionFamilies = computed(() => {
  const present = new Set(missions.collectionViews.map((view) => view.family))
  return COLLECTION_FAMILIES.filter((family) => present.has(family))
})

const dailyPending = computed(() => missions.pendingCount + missions.railPending)
const rankedSteps = computed(() =>
  Array.from({ length: rankedCount }, (_, index) => index + 1),
)

const lastCallReset = computed(() => {
  if (!lastCall.open) return t('missions.lastCallFrozen')
  const end = new Date(lastCall.endsAt - 1)
  return t('missions.lastCallUntil', {
    date: end.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }),
  })
})

function lastCallPct(target: number): number {
  if (target <= 0) return 0
  return Math.min(100, Math.round((lastCall.played / target) * 100))
}

const resetLabel = computed(() => {
  const ms = remainingMs.value
  const totalMin = Math.floor(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return t('missions.resetHint', { h, m })
})

function categoryOf(id: MissionId): string {
  return missionDef(id)?.category ?? 'presence'
}

function pct(row: { progress: number; target: number }): number {
  if (row.target <= 0) return 0
  return Math.min(100, Math.round((row.progress / row.target) * 100))
}

function isPending(id: MissionId): boolean {
  return missions.pendingIds.includes(id)
}

async function onClaimRanked(step: number, event: Event): Promise<void> {
  if (missions.busy || !rankedPending(step)) return
  if (rankedCoins(step) > 0) {
    const origin = event.currentTarget instanceof Element
      ? event.currentTarget
      : null
    await flyCoinToWallet(origin)
  }
  await missions.collectRanked(step)
  maybeShowRankedWin()
}

async function onClaim(id: MissionId, event: Event): Promise<void> {
  if (missions.busy) return
  const origin = missionCardEl(id)
    ?? (event.currentTarget instanceof Element ? event.currentTarget : null)
  await flyCoinToWallet(origin)
  await missions.collect(id)
}

async function onClaimRunner(id: string, event: Event): Promise<void> {
  if (missions.busy) return
  const row = missions.runnerRows.find((item) => item.id === id)
  if (!row?.done || row.claimed) return
  const origin = event.currentTarget instanceof Element ? event.currentTarget : null
  await flyCoinToWallet(origin)
  await missions.collectRunner(row.id)
}

function setPackNode(step: number, el: unknown): void {
  if (el instanceof HTMLElement) rankedPackNodes.set(step, el)
  else rankedPackNodes.delete(step)
}

function rankedCoins(step: number): number {
  return rankedStepCoins(step)
}

function rankedPack(step: number): boolean {
  return rankedStepGivesPack(step)
}

function rankedPackCount(step: number): number {
  return rankedStepPacks(step)
}

function rankedDone(step: number): boolean {
  return missions.rankedPlayed >= step
}

function rankedPending(step: number): boolean {
  return rankedDone(step) && step > missions.rankedCollected
}

function rankedCurrent(step: number): number {
  return Math.min(missions.rankedPlayed, step)
}

function rankedPct(step: number): number {
  if (step <= 0) return 0
  return Math.min(100, Math.round((rankedCurrent(step) / step) * 100))
}

function rankedTitle(step: number): string {
  if (step === 1) return t('missions.rankedOne')
  return t('missions.rankedMany', { n: step })
}

function viewsOf(family: CollectionFamily): CollectionTrackView[] {
  return missions.collectionViews.filter((view) => view.family === family)
}

function collectionTitle(view: CollectionTrackView): string {
  const row = view.focus
  const n = row.target
  if (view.family === 'total') {
    return row.whole
      ? t('missions.collection.totalAll')
      : t('missions.collection.total', { n })
  }
  if (view.family === 'set') {
    return row.whole
      ? t('missions.collection.setAll', { set: view.key })
      : t('missions.collection.set', { n, set: view.key })
  }
  if (view.family === 'kind') {
    return row.whole
      ? t(`missions.collection.kindAll.${view.key}`)
      : t('missions.collection.kind', {
          n,
          kind: t(`missions.collection.kinds.${view.key}`),
        })
  }
  if (view.family === 'attribute') {
    const attr = t(`card.attr.${view.key}`)
    return row.whole
      ? t('missions.collection.attrAll', { attr })
      : t('missions.collection.attr', { n, attr })
  }
  if (view.family === 'race') {
    const race = t(`card.race.${view.key}`)
    return row.whole
      ? t('missions.collection.raceAll', { race })
      : t('missions.collection.race', { n, race })
  }
  if (view.family === 'foil') {
    return t('missions.collection.foil', {
      n,
      foil: t(`missions.collection.foils.${view.key}`),
    })
  }
  return t('missions.collection.playset', { n })
}

function packsGranted(ids: readonly string[]): number {
  let total = 0
  for (const id of ids) {
    const row = missions.collectionRows.find((item) => item.id === id)
    total += row?.packs ?? 0
  }
  return total
}

function openCollectionWin(origin: Element | null, packs: number): void {
  collectionWinPacks.value = packs
  if (!origin) {
    collectionWinOrigin.value = { dx: 0, dy: 80 }
  } else {
    const rect = origin.getBoundingClientRect()
    collectionWinOrigin.value = {
      dx: rect.left + rect.width / 2 - window.innerWidth / 2,
      dy: rect.top + rect.height / 2 - window.innerHeight * 0.42,
    }
  }
  showCollectionWin.value = true
}

async function onClaimCollection(
  view: CollectionTrackView,
  event: Event,
): Promise<void> {
  const row = view.focus
  if (missions.busy || !row.done || row.claimed) return
  const origin = event.currentTarget instanceof Element ? event.currentTarget : null
  const result = await missions.collectCollection(row.id)
  if (!result || result.claimed.length === 0) return
  if (row.coins > 0) await flyCoinToWallet(origin)
  const packs = packsGranted(result.claimed)
  if (packs > 0) openCollectionWin(origin, packs)
}

function setPane(next: MissionPane): void {
  pane.value = next
  const query =
    next === 'runner'
      ? { tab: 'runner' }
      : next === 'ranked'
        ? { tab: 'ranked' }
        : next === 'collection'
        ? { tab: 'collection' }
        : next === 'lastCall'
          ? { tab: 'last-call' }
          : {}
  void router.replace({ name: 'daily-missions', query })
}

function rankedWinKey(step: number): string {
  if (step === rankedCount) return `${RANKED_WIN_PREFIX}${missions.day}`
  return `${RANKED_WIN_PREFIX}${step}:${missions.day}`
}

function hasSeenRankedWin(step: number): boolean {
  if (!missions.day) return true
  try {
    return localStorage.getItem(rankedWinKey(step)) === '1'
  } catch {
    return false
  }
}

function markRankedWin(step: number): void {
  if (!missions.day) return
  try {
    localStorage.setItem(rankedWinKey(step), '1')
  } catch {
    /* private mode */
  }
}

function nextRankedPackWin(): number | null {
  const steps = [RANKED_MID_PACK_STEP, rankedCount]
  for (const step of steps) {
    if (hasSeenRankedWin(step)) continue
    if (missions.rankedPlayed < step || missions.rankedCollected < step) continue
    const granted = step === rankedCount
      ? missions.rankedPackGranted
      : missions.rankedStep7PackGranted
    if (!granted) continue
    return step
  }
  return null
}

function captureRankedOrigin(step: number): void {
  const el = rankedPackNodes.get(step)
  if (!el) {
    rankedWinOrigin.value = { dx: 0, dy: 80 }
    return
  }
  const rect = el.getBoundingClientRect()
  rankedWinOrigin.value = {
    dx: rect.left + rect.width / 2 - window.innerWidth / 2,
    dy: rect.top + rect.height / 2 - window.innerHeight * 0.42,
  }
}

function maybeShowRankedWin(): void {
  if (pane.value !== 'ranked' || showRankedWin.value) return
  const step = nextRankedPackWin()
  if (step === null) return
  void nextTick(() => {
    if (pane.value !== 'ranked' || showRankedWin.value) return
    const ready = nextRankedPackWin()
    if (ready === null) return
    rankedWinStep.value = ready
    rankedWinPacks.value = rankedStepPacks(ready)
    captureRankedOrigin(ready)
    showRankedWin.value = true
  })
}

function onRankedWinClose(): void {
  markRankedWin(rankedWinStep.value)
  showRankedWin.value = false
  void nextTick(() => maybeShowRankedWin())
}

function rewardOrigins(): Element[] {
  const origins: Element[] = []
  if (pane.value === 'ranked') {
    document.querySelectorAll('[data-ranked-step]').forEach((el) => {
      origins.push(el)
    })
    return origins
  }
  for (const id of missions.pendingIds) {
    const el = missionCardEl(id)
    if (el) origins.push(el)
  }
  document.querySelectorAll('.rail .node.pending').forEach((el) => {
    origins.push(el)
  })
  return origins
}

async function onCollectAll(event: Event): Promise<void> {
  if (missions.busy || !canCollectAll.value) return
  if (pane.value === 'collection') {
    const marked = [...document.querySelectorAll('[data-collection-pending]')]
    const fallback = event.currentTarget instanceof Element
      ? event.currentTarget
      : null
    const result = await missions.collectAllCollection()
    if (!result || result.claimed.length === 0) return
    const from = marked.length > 0 ? marked : (fallback ? [fallback] : [])
    const flights = from.map((el, index) => flyCoinToWallet(el, index * 60))
    await Promise.all(flights)
    const packs = packsGranted(result.claimed)
    if (packs > 0) openCollectionWin(fallback, packs)
    return
  }
  const origins = rewardOrigins()
  const fallback = event.currentTarget instanceof Element
    ? event.currentTarget
    : null
  const from = origins.length > 0 ? origins : (fallback ? [fallback] : [])
  const flights = from.map((el, index) => flyCoinToWallet(el, index * 60))
  await missions.collectAll()
  await Promise.all(flights)
}

function onRetry(): void {
  void missions.refresh()
}

watch(
  () => route.query.tab,
  (tab) => {
    pane.value = paneFromQuery(tab)
  },
)

watch(
  () => [
    pane.value,
    missions.day,
    missions.rankedPackGranted,
    missions.rankedStep7PackGranted,
    missions.rankedPlayed,
    missions.rankedCollected,
  ] as const,
  () => {
    maybeShowRankedWin()
  },
)

onMounted(() => {
  void missions.refresh()
  maybeShowRankedWin()
  tick = setInterval(() => {
    remainingMs.value = msUntilUtcMidnight()
  }, 30_000)
})

onUnmounted(() => {
  if (tick) clearInterval(tick)
})
</script>

<style scoped>
.daily-missions {
  width: min(960px, 100%);
  margin: 0 auto;
  padding: 32px 16px 48px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.pane-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 16px;
}

.pane-tab {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.pane-tab.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.tab-badge {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--md-sys-color-error);
  color: var(--md-sys-color-on-error);
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pack-prize {
  display: block;
  width: 28px;
  height: 36px;
  border-radius: 4px;
  overflow: hidden;
}

.pack-prize img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reward-shot {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 104px;
  margin: 0 0 12px;
  border-radius: 10px;
  background:
    radial-gradient(80% 80% at 50% 0%, rgba(62, 198, 255, 0.18), transparent 60%),
    #071422;
  overflow: hidden;
}

.reward-shot img {
  display: block;
  max-height: 96px;
  width: auto;
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.4));
}

.reward-shot.mat img {
  width: 100%;
  max-height: 88px;
  object-fit: cover;
  border-radius: 6px;
}

.reward-shot.pack img {
  height: 96px;
  border-radius: 4px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.support {
  margin: 8px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.text-btn {
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}

.retry {
  display: inline;
  margin-left: 8px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.reset,
.empty {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}

.family-title {
  margin: 8px 0 10px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.collection-groups {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.prize {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rungs {
  display: flex;
  gap: 6px;
  margin: 0 0 10px;
}

.rung {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--md-sys-color-outline-variant);
}

.rung.done,
.rung.ready {
  background: var(--md-sys-color-primary);
}

.rung.ready {
  box-shadow: 0 0 0 2px var(--md-sys-color-primary-container);
}

.card.ready {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.md-panel.card {
  margin: 0;
  padding: 16px 18px 14px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.card.done {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cat {
  margin: 0;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.coin-prize {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--md-sys-color-primary);
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

h2 {
  margin: 8px 0 12px;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
}

.bar {
  height: 10px;
  border-radius: 999px;
  background: var(--md-sys-color-outline-variant);
  overflow: hidden;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--mission-bar, var(--md-sys-color-primary));
  transition: width var(--md-motion-medium) var(--md-motion-emphasized);
}

.card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}

.count,
.claimed {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.claim {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.claim:disabled {
  opacity: 0.6;
  cursor: default;
}

@media (min-width: 800px) {
  .collection-groups .list {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  h1 { font-size: 28px; line-height: 36px; }
  .page-header { flex-direction: column; }
  .header-actions { align-items: flex-start; }
}
</style>
